import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableColumn } from "react-data-table-component";
import * as XLSX from "sheetjs-style";
import { useDispatch } from "react-redux";
import { updateDashboardFields } from "../../../features/common/commonSlice";
import { toast } from "react-toastify";
const FaFileExcel = require("react-icons/fa").FaFileExcel;

interface MeetingData {
  state_name: string;
  state_code: number;
  total_bps: number;
  bps_with_mom_generated: number;
  total_meetings_uploaded: number;
  draft_mom: number;          
  final_submitted_mom: number; 
  isTotal?: boolean;
}

const BlockDashboard1: React.FC<{ finYear: string; meetingType: any }> = ({
  finYear,
  meetingType,
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const dispatch = useDispatch();

  const [tableData, setTableData] = useState<MeetingData[]>([]);
  const [filterText, setFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const { data } = await axios.get(`${baseUrl}api/bp_report`, {
          params: {
            fin_year: finYear,
            meeting_type: meetingType.value !== "0" ? meetingType.value : undefined,
          },
        });
        if (data?.msg_code === 400) {
          toast.error(data?.msg || "Something went wrong");
          setTableData([]);
          return;
        }

        const mappedData: MeetingData[] =
          data?.data?.map((item: any) => ({
            state_name: item.state_name,
            state_code: item.state_code,
            total_bps: item.total_bps,
            bps_with_mom_generated: item.bps_with_mom_generated,
            draft_mom: item.draft_mom || 0,
            final_submitted_mom: item.final_submitted_mom || 0,
            total_meetings_uploaded: item.total_meetings_uploaded,
          })) || [];

        setTableData(mappedData);
      } catch (error) {
        console.error("Error fetching meeting data:", error);
      }
    };

    setTableData([]);
    fetchMeetings();
  }, [finYear, meetingType, baseUrl]);

  const handleMomHighlight = (stateCode: number, stateName: string) => {
    dispatch(
      updateDashboardFields({
        menu: "Block Panchayats",
        fields: { state_code: stateCode, state_name: stateName, zp_lgd_code: null, zp_name: "", bp_lgd_code: null, bp_name: "" },
      })
    );
  };

  const columns: TableColumn<MeetingData>[] = [
    { name: "S.No.", cell: (row: any, index) => row.isTotal ? "" : (index + 1 + (currentPage - 1) * rowsPerPage).toString(), width: "80px" },
    {
      name: "State Name",
      selector: (row) => row.state_name,
      cell: (row: any) => {
        const isClickable = !row.isTotal && row.total_meetings_uploaded > 0;
        return (
          <span className={isClickable ? "highlight-td" : ""} style={{ cursor: isClickable ? "pointer" : "default" }}
            onClick={() => isClickable && handleMomHighlight(row.state_code, row.state_name)}>
            {row.state_name}
          </span>
        );
      },
      sortable: true,
      wrap: true,
    },
    { name: "Total BPs & Equivalents", selector: (row) => row.total_bps, sortable: true },
    { name: "BPs with MoM Generated", selector: (row) => row.bps_with_mom_generated, sortable: true },
    { name: "Draft MoM", selector: (row) => row.draft_mom, sortable: true },
    { name: "Final Submitted", selector: (row) => row.final_submitted_mom, sortable: true },
    { name: "Total Meetings Uploaded", selector: (row) => row.total_meetings_uploaded, sortable: true },
  ];

  const filteredData = tableData.filter((item) =>
    item.state_name?.toLowerCase().includes(filterText.toLowerCase()) || String(item.state_code).includes(filterText)
  );

  const totalRow: MeetingData = {
    state_name: "Total",
    state_code: 0,
    total_bps: filteredData.reduce((sum, r) => sum + r.total_bps, 0),
    bps_with_mom_generated: filteredData.reduce((sum, r) => sum + r.bps_with_mom_generated, 0),
    draft_mom: filteredData.reduce((sum, r) => sum + r.draft_mom, 0),
    final_submitted_mom: filteredData.reduce((sum, r) => sum + r.final_submitted_mom, 0),
    total_meetings_uploaded: filteredData.reduce((sum, r) => sum + r.total_meetings_uploaded, 0),
    isTotal: true,
  };

  const exportExcel = () => {
    const header = ["S.No.", "State Name", "Total BPs", "BPs with MoM",  "Draft MoM", "Final Submitted" ,"Total Meetings",];
    const data = filteredData.map((row, index) => [index + 1, row.state_name, row.total_bps, row.bps_with_mom_generated,  row.draft_mom, row.final_submitted_mom , row.total_meetings_uploaded,]);
    const totalsRow = ["", "Total", totalRow.total_bps, totalRow.bps_with_mom_generated, totalRow.total_meetings_uploaded, totalRow.draft_mom, totalRow.final_submitted_mom];
    
    const ws = XLSX.utils.aoa_to_sheet([header, ...data, totalsRow]);
    ws["!cols"] = [{ wch: 8 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "report.xlsx");
  };

  return (
    <div className="mt-3">
      <div className="d-flex justify-content-between mb-2">
        <input type="text" className="form-control w-25" placeholder="Search..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
        <button className="shiny-btn" onClick={exportExcel}><FaFileExcel /> Export Excel</button>
      </div>
      <DataTable
        columns={columns}
        data={filteredData.length ? [...filteredData, totalRow] : []}
        conditionalRowStyles={[{ when: (row: any) => row.isTotal, style: { fontWeight: "bold", backgroundColor: "#f8f9fa", borderTop: "2px solid #dee2e6" } }]}
        highlightOnHover
        striped
      />
    </div>
  );
};

export default BlockDashboard1;